import { Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { cn } from '@/lib/utils'
import { useComplianceScope } from '@/models/vault'

// Long enough to read a line and believe the work behind it; six steps, ~12s.
const STEP_DURATION_MS = 2000
const STEP_TRANSITION_MS = 350

export const SCANNING_LABEL = 'Scanning insurance data and documents…'

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

interface Counts {
  records: number
  answers: number
  documents: number
}

/** What the import says as it runs; the last line is the one that stays. */
function steps({ records, answers, documents }: Counts): string[] {
  return [
    SCANNING_LABEL,
    `Copying ${plural(records, 'record')}…`,
    `Pre-filling vault with ${plural(answers, 'answer')}…`,
    'Checking for relevant documents…',
    `${plural(documents, 'relevant document')} found`,
    'Finishing up…',
  ]
}

function doneLabel({ answers, documents }: Counts): string {
  return `Import complete. ${plural(answers, 'answer')} and ${plural(documents, 'document')} copied and securely stored.`
}

function StepRow({
  label,
  icon,
  exiting,
  animate = true,
}: {
  label: string
  icon: React.ReactNode
  exiting?: boolean
  animate?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 text-right max-sm:flex-wrap sm:whitespace-nowrap',
        // Only the outgoing row leaves the flow; the current one gives the wrapper its width.
        exiting && 'absolute inset-y-0 right-0',
        animate && (exiting ? 'animate-step-out' : 'animate-step-in')
      )}
    >
      {icon}
      <span className="text-ink-500 text-sm">{label}</span>
    </div>
  )
}

/** The import, narrated beside the page title: nothing until it starts, then the steps, then the receipt. */
export function VaultImportStatus({ orgId, running }: { orgId: string; running: boolean }) {
  const reduceMotion = usePrefersReducedMotion()
  const { draft } = useComplianceScope(orgId)
  const [completed, setCompleted] = useState(0)

  const counts: Counts = {
    records: draft ? draft.roster.drivers + draft.roster.trucks + draft.roster.trailers : 0,
    answers: draft?.answerCount ?? 0,
    documents: draft?.documentCount ?? 0,
  }
  const sequence = steps(counts)
  const isDone = completed >= sequence.length
  const currentIndex = Math.min(completed, sequence.length - 1)

  // Keep the outgoing line mounted just long enough to play its exit.
  const previousIndexRef = useRef(currentIndex)
  const [outgoingIndex, setOutgoingIndex] = useState<number | null>(null)

  useEffect(() => {
    const previous = previousIndexRef.current
    if (previous === currentIndex) return
    previousIndexRef.current = currentIndex
    setOutgoingIndex(previous)
    const timer = setTimeout(() => setOutgoingIndex(null), STEP_TRANSITION_MS)
    return () => clearTimeout(timer)
  }, [currentIndex])

  useEffect(() => {
    if (!running || isDone) return
    // Nothing advances until the counts exist, so no step reads a zero it does not mean.
    if (!draft) return
    const timer = setTimeout(() => setCompleted((c) => c + 1), STEP_DURATION_MS)
    return () => clearTimeout(timer)
  }, [running, isDone, completed, draft])

  if (!running) return null

  const icon = <Spinner className="size-4 shrink-0 motion-reduce:animate-none" />
  // Reduced motion pins to the last line rather than stepping, the way AiAnalysisLoading does:
  // the run still reaches the receipt, without text changing under the reader every two seconds.
  const shownIndex = reduceMotion ? sequence.length - 1 : currentIndex

  return (
    <div aria-live="polite" className="relative flex min-h-6 items-center justify-end">
      {isDone ? (
        <p className="text-ink-600 flex items-center justify-end gap-2 text-sm">
          <Check className="text-accent-green size-4 shrink-0" strokeWidth={3} />
          {doneLabel(counts)}
        </p>
      ) : (
        <>
          {!reduceMotion && outgoingIndex !== null && (
            <StepRow label={sequence[outgoingIndex]} icon={icon} exiting />
          )}
          <StepRow key={shownIndex} label={sequence[shownIndex]} icon={icon} animate={!reduceMotion} />
        </>
      )}
    </div>
  )
}
